import type { ComponentType } from 'react';
import track from 'react-tracking';
import type { DeleteHoldingModalPayload } from '@/components/DeprecatedPortfolio/components/DeleteHoldingModal/redux/interface';
import { LoadingSpinner } from '@simplywallst/ui-core';
import PositiveNegativeValue from '@/components/PositiveNegativeValue';
import { RouterLink } from '@redux-first-router-link';
import { MoreVerticalSolid } from '@simplywallst/ui-core/icons';
import { Column, ButtonWithDropdown } from '@simplywallst/ui-core';
import { queryKey as userPortfoliosQueryKey } from '@/components/DeprecatedPortfolios/components/UserPortfolios/query/getUserPortfolios';
import {
  Tile,
  Header,
  Footer,
  Ticker,
  SubMeta,
  Excluded,
  CompanyName,
  SubMetaLabel,
  CurrentValue,
  LoadingOverlay,
  HoldingWrapper,
  SnowflakeWrapper,
  MenuButtonContainer,
} from './styled';
import { useQueryClient } from 'react-query';
import loadable from '@loadable/component';

const Snowflake = loadable(() => import('@/components/Snowflake/Snowflake'));

const dropdownOptions = [
  { label: 'Edit transactions', value: 'edit' },
  { label: 'Delete', value: 'delete' },
];

type Props = {
  canonicalUrl?: string;
  capitalGainWeek: number;
  capitalGainWeekFormatted: string;
  companyId: string;
  companyName?: string;
  currentDividend?: number;
  currentDividendFormatted?: string;
  currentValueFormatted?: string | number;
  dividendReturn?: number;
  dividendReturnFormatted?: string;
  excluded: boolean;
  index: number;
  isEditable?: boolean;
  isUpdating: boolean;
  isWatchlist: boolean;
  portfolioWithNoTransactions: boolean;
  itemId: string;
  portfolioId: string | null;
  sharePrice: number;
  sharePriceFormatted: string;
  showRemoveButton: boolean;
  snowflakePoints: number[];
  tickerSymbol: string;
  uniqueSymbol?: string;
  onDelete: (data: DeleteHoldingModalPayload) => void;
  onEdit: (data: { itemId: string }) => void;
};

const Holding = ({
  canonicalUrl,
  capitalGainWeek,
  capitalGainWeekFormatted,
  companyId,
  companyName,
  currentValueFormatted,
  dividendReturn,
  dividendReturnFormatted,
  isEditable,
  isUpdating,
  itemId,
  index,
  portfolioId,
  sharePriceFormatted,
  snowflakePoints,
  tickerSymbol,
  tracking: { trackEvent },
  uniqueSymbol,
  isWatchlist,
  portfolioWithNoTransactions,
  onDelete,
  onEdit,
  excluded,
}: Props & Tracking) => {
  const queryClient = useQueryClient();
  const hasNoTransactions = currentValueFormatted === 'No transactions';
  const isLiquidated = currentValueFormatted === 'Liquidated';
  const handleClick = () => {
    trackEvent({
      action: 'click',
      modifier: 'more',
    });
  };
  if (!canonicalUrl) return null;
  return (
    <HoldingWrapper>
      <RouterLink
        noUnderline
        inheritColor
        to={canonicalUrl}
        data-cy-id={`holding-link-${itemId}`}
        onClick={() =>
          trackEvent({
            action: 'click',
            companyId: companyId,
            uniqueSymbol: uniqueSymbol,
            listIndex: index,
          })
        }
      >
        <Tile>
          <Header as="header" alignContent="center">
            <SnowflakeWrapper>
              <Snowflake points={snowflakePoints || []} disableLabel={true} />
            </SnowflakeWrapper>
            <Column column>
              <Ticker>
                <span>{tickerSymbol}</span>
                {sharePriceFormatted}
              </Ticker>
              <CompanyName>{companyName}</CompanyName>
            </Column>
          </Header>
          <Footer>
            {excluded ? (
              <Excluded>Excluded from analysis due to plan limit</Excluded>
            ) : (
              <>
                {/* This is a spacer using the min-width attr of the SubMeta component */}
                {isWatchlist && <SubMeta />}
                <SubMeta>
                  <PositiveNegativeValue value={capitalGainWeek / 100}>
                    {/* TODO: Shouldn't this formatted value be formatted with a `%` ? */}
                    {capitalGainWeekFormatted}%
                  </PositiveNegativeValue>
                  <SubMetaLabel>7 Day</SubMetaLabel>
                </SubMeta>
                <SubMeta>
                  <PositiveNegativeValue value={dividendReturn || 0}>
                    {dividendReturnFormatted}
                  </PositiveNegativeValue>
                  <SubMetaLabel>
                    {isWatchlist || portfolioWithNoTransactions
                      ? `1 year`
                      : `Total`}
                  </SubMetaLabel>
                </SubMeta>
                {!isWatchlist && (
                  <SubMeta noTransactions={hasNoTransactions}>
                    <CurrentValue liquidated={isLiquidated}>
                      {currentValueFormatted}
                    </CurrentValue>
                    <SubMetaLabel>Holding</SubMetaLabel>
                  </SubMeta>
                )}
              </>
            )}
          </Footer>
        </Tile>
      </RouterLink>
      {isEditable && (
        <MenuButtonContainer>
          <ButtonWithDropdown
            onClick={handleClick}
            options={dropdownOptions}
            onSelect={(type, label) => {
              trackEvent({
                action: 'click',
                modifier: label,
              });
              switch (type) {
                case 'edit':
                  onEdit({
                    itemId: itemId,
                  });
                  break;
                case 'delete':
                  queryClient.invalidateQueries(userPortfoliosQueryKey);
                  onDelete({
                    companyId,
                    companyName: companyName || '',
                    portfolioId: portfolioId || '',
                    uniqueSymbol: uniqueSymbol || '',
                  });
                  break;
                default:
                  return;
              }
            }}
            data-cy-id={`holding-menu-${itemId}`}
          >
            <MoreVerticalSolid />
          </ButtonWithDropdown>
        </MenuButtonContainer>
      )}
      {isUpdating && (
        <LoadingOverlay>
          <LoadingSpinner light top="50%" left="50%" />
        </LoadingOverlay>
      )}
    </HoldingWrapper>
  );
};

Holding.defaultProps = {
  companyName: '',
  canonicalUrl: '',
  uniqueSymbol: '',
  isEditable: false,
  dividendReturnFormatted: '0.00',
  dividendReturn: 0,
  currentValueFormatted: '0.00',
};

const ComponentWithType = track(
  ({ index, companyId, uniqueSymbol, liquidated, excluded }) => ({
    type: 'track',
    subject: 'holding_tile',
    index,
    companyId,
    uniqueSymbol,
    liquidated,
    excluded,
  })
)(Holding) as ComponentType<Props>;

export default ComponentWithType;
